
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { RefreshCw, Copy, CheckCircle } from "lucide-react";
import { useState, useRef } from "react";
import { useToast } from "@/components/ui/use-toast";
import { sanitizeUserContent } from "@/utils/sanitizer";

interface ReviewDisplayProps {
  profileReview: string;
  onReset: () => void;
}

const ReviewDisplay = ({ profileReview, onReset }: ReviewDisplayProps) => {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();
  const textRef = useRef<HTMLDivElement>(null);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(profileReview);
      setCopied(true);
      toast({
        title: "Copied to clipboard",
        description: "The review has been copied to your clipboard",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({
        title: "Failed to copy",
        description: "Could not copy to clipboard. Try selecting and copying manually.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <Card className="p-6 overflow-auto bg-background/50 shadow-inner border border-muted/30 rounded-lg">
        <div
          ref={textRef}
          className="prose max-w-none whitespace-pre-wrap text-base leading-relaxed"
          style={{ minHeight: '250px' }}
          dangerouslySetInnerHTML={{ __html: sanitizeUserContent(profileReview.replace(/\n/g, "<br />")) }}
        />
      </Card>

      <div className="flex justify-end gap-3 mt-4">
        <Button variant="outline" onClick={onReset} className="gap-2 px-4 py-2">
          <RefreshCw className="h-4 w-4" />
          <span>New Review</span>
        </Button>
        <Button 
          onClick={handleCopy} 
          className="gap-2 px-4 py-2"
          variant={copied ? "outline" : "default"}
        >
          {copied ? (
            <>
              <CheckCircle className="h-4 w-4" />
              <span>Copied!</span>
            </>
          ) : (
            <>
              <Copy className="h-4 w-4" />
              <span>Copy</span>
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default ReviewDisplay;
